// Open BSV License version 5
// Copyright © 2025 The Bitcoin Corporation LTD
// Registered in England and Wales • Company No. 16735102
// This software can only be used on BSV blockchains

import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import FeaturesPage from './pages/FeaturesPage';
import TokenPage from './pages/TokenPage';
import BWriterContributionsPage from './pages/BWriterContributionsPage';
import DocsPage from './pages/DocsPage';
import TasksPage from './pages/TasksPage';
import ContractsPage from './pages/ContractsPage';
import PlatformPage from './pages/PlatformPage';
import SignupPage from './pages/SignupPage';
import CommissionsPage from './pages/CommissionsPage';
import OfferPage from './pages/OfferPage';
import OffersPage from './pages/OffersPage';
import GrantsPage from './pages/GrantsPage';
import ApiPage from './pages/ApiPage';
import ChangelogPage from './pages/ChangelogPage';
import StatusPage from './pages/StatusPage';
import DeveloperContractsPage from './pages/DeveloperContractsPage';
import PublisherOfferPage from './pages/PublisherOfferPage';
import AuthorOffersPage from './pages/AuthorOffersPage';
import DevelopersGrantsPage from './pages/DevelopersGrantsPage';
import AuthorsGrantsPage from './pages/AuthorsGrantsPage';
import PublishersGrantsPage from './pages/PublishersGrantsPage';
import ImportPage from './pages/ImportPage';
import EncryptPage from './pages/EncryptPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import BookStore from './components/BookStore';
// import DocumentSidebar from './components/DocumentSidebar'; // Removed for bookstore"
import BapPage from './pages/BapPage';
import MAIPPage from './pages/MAIPPage';
import { BlockchainDocumentService, BlockchainDocument } from './services/BlockchainDocumentService';
import { HandCashService, HandCashUser } from './services/HandCashService';
import { GoogleAuthProvider } from './components/GoogleAuth';
import UnifiedAuth from './components/UnifiedAuth';
import CleanTaskbar from './components/CleanTaskbar';
import Footer from './components/Footer';
import ProofOfConceptBanner from './components/ProofOfConceptBanner';
import DevSidebar from './components/DevSidebar';
import DocumentExchangeView from './components/DocumentExchangeView';
import BitcoinAppsView from './components/BitcoinAppsView';
import BitcoinAppOverviews from './components/BitcoinAppOverviews';
import { BitcoinAppEvents } from './utils/appEvents';
import { cleanupEmptyDocuments } from './utils/cleanupDocuments';
import { useBitcoinOS } from './utils/useBitcoinOS';
import ServiceWorkerRegistration from './components/ServiceWorkerRegistration';
import LoadingDoor from './components/LoadingDoor';

function App() {
  const [documentService, setDocumentService] = useState<BlockchainDocumentService | null>(null);
  const [handcashService] = useState<HandCashService>(new HandCashService());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isInOS, setTitle } = useBitcoinOS();
  const [currentUser, setCurrentUser] = useState<HandCashUser | null>(null);
  const [googleUser, setGoogleUser] = useState<any>(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingDoor, setShowLoadingDoor] = useState(() => {
    // Only show loading door on first visit in this session
    const hasShownDoor = sessionStorage.getItem('hasShownLoadingDoor');
    return !hasShownDoor;
  });
  const [, setCurrentDocument] = useState<BlockchainDocument | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showBitcoinMenu, setShowBitcoinMenu] = useState(false);
  const [showBooksMenu, setShowBooksMenu] = useState(false);
  const [showDevelopersMenu, setShowDevelopersMenu] = useState(false);
  const [, setSidebarRefresh] = useState(0);
  const [showExchange, setShowExchange] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showBitcoinApps, setShowBitcoinApps] = useState(false);
  const [activeAppOverview, setActiveAppOverview] = useState<string | null>(null);
  const [publishedDocuments] = useState<BlockchainDocument[]>([]);
  const [devSidebarCollapsed, setDevSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('devSidebarCollapsed');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for Document Exchange open event
  useEffect(() => {
    const handleOpenExchange = () => setShowExchange(true);
    window.addEventListener('openDocumentExchange', handleOpenExchange);
    return () => window.removeEventListener('openDocumentExchange', handleOpenExchange);
  }, []);

  // Listen for Features page open event
  useEffect(() => {
    const handleShowFeatures = () => {
      setShowFeatures(true);
      setShowExchange(false); // Close exchange if open
    };
    window.addEventListener('showFeaturesPage', handleShowFeatures);
    return () => window.removeEventListener('showFeaturesPage', handleShowFeatures);
  }, []);

  // Listen for Bitcoin Apps open event
  useEffect(() => {
    const handleOpenBitcoinApps = () => setShowBitcoinApps(true);
    window.addEventListener('loadBitcoinApps', handleOpenBitcoinApps);
    return () => window.removeEventListener('loadBitcoinApps', handleOpenBitcoinApps);
  }, []);

  // Listen for individual app events
  useEffect(() => {
    const appEventHandlers: { [key: string]: EventListener } = {};
    
    // Create handlers for each app except Exchange (which already has its own handler)
    Object.values(BitcoinAppEvents).forEach(eventName => {
      if (eventName !== BitcoinAppEvents.EXCHANGE && eventName !== BitcoinAppEvents.APPS) {
        appEventHandlers[eventName] = () => {
          setActiveAppOverview(eventName);
          setShowBitcoinApps(false);
          setShowExchange(false);
        };
        window.addEventListener(eventName, appEventHandlers[eventName]);
      }
    });

    return () => {
      Object.entries(appEventHandlers).forEach(([eventName, handler]) => {
        window.removeEventListener(eventName, handler);
      });
    };
  }, []);

  // Clean up empty/test documents on startup
  useEffect(() => {
    const deletedCount = cleanupEmptyDocuments();
    if (deletedCount > 0) {
      console.log(`Cleaned up ${deletedCount} empty/test documents`);
      // Refresh the sidebar to reflect the cleanup
      setSidebarRefresh(prev => prev + 1);
    }
  }, []);

  // Set app title when running in Bitcoin OS
  useEffect(() => {
    if (isInOS) {
      setTitle('Bitcoin Books');
    }
  }, [isInOS, setTitle]);

  const initializeDocumentService = useCallback(async () => {
    const service = new BlockchainDocumentService(handcashService);
    await service.reconnect();
    setDocumentService(service);
  }, [handcashService]);

  const checkAuthentication = useCallback(() => {
    // Check if user is already logged in
    if (handcashService.isAuthenticated()) {
      const user = handcashService.getCurrentUser();
      setCurrentUser(user);
      setIsAuthenticated(true);
      initializeDocumentService();
    }
    setIsLoading(false);
  }, [handcashService, initializeDocumentService]);

  const handleLogin = useCallback((user: HandCashUser) => {
    console.log('=== User Successfully Authenticated ===');
    console.log('User handle:', user.handle);
    console.log('User paymail:', user.paymail);
    console.log('=====================================');
    setCurrentUser(user);
    setIsAuthenticated(true);
    initializeDocumentService();
  }, [initializeDocumentService]);

  useEffect(() => {
    // Check for Google user first
    const storedGoogleUser = localStorage.getItem('googleUser');
    if (storedGoogleUser) {
      try {
        const user = JSON.parse(storedGoogleUser);
        setGoogleUser(user);
        console.log('Google user restored:', user);
      } catch (error) {
        console.error('Failed to parse Google user:', error);
      }
    }

    // BRC-100 wallet session (bWalletX): restore it, and follow sign-in/sign-out from anywhere in the app.
    checkAuthentication();
    const onWalletChange = () => {
      const user = handcashService.getCurrentUser();
      if (user) handleLogin(user);
      else { setIsAuthenticated(false); setCurrentUser(null); }
    };
    window.addEventListener('bwx-wallet-change', onWalletChange);
    return () => window.removeEventListener('bwx-wallet-change', onWalletChange);
  }, [checkAuthentication, handcashService, handleLogin]);

  const handleLogout = () => {
    console.log('=== COMPLETE LOGOUT ===');
    
    // Use HandCash service logout
    handcashService.logout();
    
    // Clear EVERYTHING
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear all cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Reset state
    setIsAuthenticated(false);
    setCurrentUser(null);
    setDocumentService(null);
    setCurrentDocument(null);
    
    console.log('Logout complete - refreshing page...');
    
    // Force hard reload to clear all memory
    setTimeout(() => {
      window.location.replace('/');
    }, 100);
  };

  return (
    <>
      <ServiceWorkerRegistration />
      {/* Loading Door Animation - Only on first visit */}
      {showLoadingDoor && (
        <LoadingDoor onComplete={() => {
          setShowLoadingDoor(false);
          sessionStorage.setItem('hasShownLoadingDoor', 'true');
        }} />
      )}
      {/* Proof of Concept Banner - positioned at the very top */}
      {!isInOS && <ProofOfConceptBanner />}
      
      <GoogleAuthProvider>
        {/* Global elements that appear on all pages */}
        {!isLoading && (
          <>
            {/* Developer Sidebar - Desktop Only */}
            {!isMobile && !isInOS && <DevSidebar onCollapsedChange={setDevSidebarCollapsed} />}
            
            {/* Clean taskbar with proper spacing */}
            {!isInOS && <CleanTaskbar
              isAuthenticated={isAuthenticated}
              currentUser={currentUser}
              onLogout={handleLogout}
              onNewDocument={() => {
                setCurrentDocument(null);
                setShowExchange(false);
              }}
              onSaveDocument={() => {
                const saveBtn = document.querySelector('.save-btn-mobile, [title*="Save"]') as HTMLElement;
                saveBtn?.click();
              }}
              onOpenTokenizeModal={() => {
                window.dispatchEvent(new CustomEvent('openTokenizeModal'));
              }}
              onOpenTwitterModal={() => {
                window.dispatchEvent(new CustomEvent('openTwitterModal'));
              }}
              documentService={documentService}
              onToggleAIChat={() => setShowAIChat(!showAIChat)}
            />}
          </>
        )}

        <Routes>
      <Route path="/bitcoin-writer/bap" element={<BapPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/token" element={<TokenPage />} />
      <Route path="/tasks" element={<TasksPage />} />
      <Route path="/contracts" element={<ContractsPage />} />
      <Route path="/import" element={<ImportPage />} />
      <Route path="/encrypt" element={<EncryptPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Developer Routes - Symmetrical */}
      <Route path="/developer/offer" element={<OfferPage />} />
      <Route path="/developer/offers" element={<DeveloperContractsPage />} />
      
      {/* Author Routes - Symmetrical */}
      <Route path="/author/offer" element={<OfferPage />} />
      <Route path="/author/offers" element={<AuthorOffersPage />} />
      
      {/* Publisher Routes - Symmetrical */}
      <Route path="/publisher/offer" element={<PublisherOfferPage />} />
      <Route path="/publisher/offers" element={<OffersPage />} />
      
      {/* Grants Routes */}
      <Route path="/developers/grants" element={<DevelopersGrantsPage />} />
      <Route path="/authors/grants" element={<AuthorsGrantsPage />} />
      <Route path="/publishers/grants" element={<PublishersGrantsPage />} />
      
      {/* Other Routes */}
      <Route path="/contributions" element={<BWriterContributionsPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/enterprise" element={<CommissionsPage />} />
      <Route path="/grants" element={<GrantsPage />} />
      <Route path="/maip" element={<MAIPPage />} />
      <Route path="/api" element={<ApiPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/*" element={
        isLoading ? (
          <div className="App">
            <div className="loading">Loading Bitcoin Books...</div>
          </div>
        ) : (
          <>
          <div className="App">
            
            {/* Old macOS-style taskbar (kept for reference, can be removed later) */}
            <div className="taskbar" style={{display: 'none'}}>
              <div className="taskbar-left">
                <div className="bitcoin-menu-container">
                  <button 
                    className="bitcoin-logo-button"
                    onClick={() => setShowBitcoinMenu(!showBitcoinMenu)}
                    aria-label="Bitcoin Menu"
                  >
                    <span className="bitcoin-logo">₿</span>
                  </button>
                  {showBitcoinMenu && (
                    <>
                      <div className="menu-overlay" onClick={() => setShowBitcoinMenu(false)} />
                      <div className="bitcoin-menu">
                        <div className="menu-header">
                          <div className="bitcoin-logo-small">₿</div>
                          <span>Bitcoin Books</span>
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          window.location.href = '/';
                          setShowBitcoinMenu(false);
                        }}>
                          <span>📚</span> Bitcoin Books
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          alert('Bitcoin Books v1.0\n\nCurated Bitcoin and cryptocurrency book marketplace\n\n© 2025 The Bitcoin Corporation LTD\nBuilt on Bitcoin SV blockchain');
                          setShowBitcoinMenu(false);
                        }}>
                          <span>ℹ️</span> About Bitcoin Books
                        </div>
                        <div className="menu-separator" />
                        {isAuthenticated && (
                          <div className="menu-item" onClick={() => {
                            handleLogout();
                            setShowBitcoinMenu(false);
                          }}>
                            <span>🚪</span> Sign Out
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                
                <div className="books-menu-container">
                  <button 
                    className="books-menu-button"
                    onClick={() => setShowBooksMenu(!showBooksMenu)}
                    aria-label="Books Menu"
                  >
                    Bitcoin Books
                  </button>
                  {showBooksMenu && (
                    <>
                      <div className="menu-overlay" onClick={() => setShowBooksMenu(false)} />
                      <div className="books-menu">
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Open
                          alert('Open functionality coming soon');
                          setShowBooksMenu(false);
                        }}>
                          <span>📂</span> Open
                        </div>
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Save
                          (document.querySelector('.save-btn-mobile, [title*="Save"]') as HTMLElement)?.click();
                          setShowBooksMenu(false);
                        }}>
                          <span>💾</span> Save
                        </div>
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Save As
                          alert('Save As functionality coming soon');
                          setShowBooksMenu(false);
                        }}>
                          <span>📋</span> Save As
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Encrypt
                          (document.querySelector('[title*="Encrypt"]') as HTMLElement)?.click();
                          setShowBooksMenu(false);
                        }}>
                          <span>🔒</span> Encrypt
                        </div>
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Decrypt
                          (document.querySelector('[title*="Decrypt"]') as HTMLElement)?.click();
                          setShowBooksMenu(false);
                        }}>
                          <span>🔓</span> Decrypt
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          // Dispatch event to open tokenize modal
                          window.dispatchEvent(new CustomEvent('openTokenizeModal'));
                          setShowBooksMenu(false);
                        }}>
                          <span>🎨</span> Create Bitcoin Asset
                        </div>
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Paywall
                          (document.querySelector('[title*="Set price"]') as HTMLElement)?.click();
                          setShowBooksMenu(false);
                        }}>
                          <span>💰</span> Paywall
                        </div>
                        <div className="menu-item" onClick={() => {
                          // TODO: Implement Publish
                          (document.querySelector('[title*="Publish"]') as HTMLElement)?.click();
                          setShowBooksMenu(false);
                        }}>
                          <span>🌍</span> Publish
                        </div>
                        <div className="menu-item" onClick={() => {
                          // Dispatch event to open Twitter modal
                          window.dispatchEvent(new CustomEvent('openTwitterModal'));
                          setShowBooksMenu(false);
                        }}>
                          <span>🐦</span> Post to Twitter
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="developers-menu-container">
                  <button 
                    className="developers-menu-button"
                    onClick={() => setShowDevelopersMenu(!showDevelopersMenu)}
                    aria-label="Developers Menu"
                  >
                    Developers
                  </button>
                  {showDevelopersMenu && (
                    <>
                      <div className="menu-overlay" onClick={() => setShowDevelopersMenu(false)} />
                      <div className="developers-menu">
                        <div className="menu-header">
                          <span>Developer Resources</span>
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          window.open('/bitcoin-writer/bap', '_blank');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>📑</span> BAP Executive Summary
                        </div>
                        <div className="menu-item" onClick={() => {
                          window.open('https://github.com/bitcoin-apps-suite/bitcoin-writer', '_blank');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>📂</span> GitHub Repository
                        </div>
                        <div className="menu-item" onClick={() => {
                          window.open('https://bwalletx.com', '_blank');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>📚</span> bWalletX (BRC-100)
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          window.open('https://github.com/bitcoin-apps-suite/blockchain-spreadsheet', '_blank');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>📊</span> Blockchain Spreadsheet
                        </div>
                        <div className="menu-item" onClick={() => {
                          window.open('https://github.com/bitcoin-apps-suite/bitcoin-drive', '_blank');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>💾</span> Bitcoin Drive
                        </div>
                        <div className="menu-separator" />
                        <div className="menu-item" onClick={() => {
                          alert('Bitcoin Writer API\n\nEndpoints:\n• POST /api/documents - Create document\n• GET /api/documents - List documents\n• GET /api/documents/:id - Get document\n• DELETE /api/documents/:id - Delete document\n\nBuilt on Bitcoin SV blockchain');
                          setShowDevelopersMenu(false);
                        }}>
                          <span>🔌</span> API Documentation
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="taskbar-center">
                {/* Window controls area */}
              </div>
              <div className="taskbar-right">
                <a 
                  href="https://x.com/bitcoin_writer" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="taskbar-link"
                  aria-label="Follow on X"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>
            
            <header className="App-header">
              
              {/* Logo and title in center */}
              <div className="title-section">
                <div className="app-title-container">
                  <img 
                    src="/logo.svg" 
                    alt="Bitcoin Writer Logo" 
                    className="app-logo"
                    style={{
                      width: '32px',
                      height: '32px',
                      marginRight: '12px'
                    }}
                  />
                  <h1 
                    onClick={() => {
                      setShowFeatures(false);
                      setShowExchange(false);
                      setShowBitcoinApps(false);
                      setActiveAppOverview(null);
                    }}
                    style={{
                      cursor: 'pointer'
                    }}
                    title="Return to main view"
                  >
                    <span style={{color: '#ff9500'}}>Bitcoin</span> Books
                  </h1>
                </div>
                <p className="app-subtitle">Discover and purchase Bitcoin and cryptocurrency books</p>
              </div>
              
              {/* Auth and mobile menu on the right */}
              <div className="header-right">
                <UnifiedAuth
                  googleUser={googleUser}
                  setGoogleUser={setGoogleUser}
                  isHandCashAuthenticated={isAuthenticated}
                  currentHandCashUser={currentUser}
                  handcashService={handcashService}
                  onHandCashLogin={() => handcashService.login()}
                  onHandCashLogout={handleLogout}
                />
                <button 
                  className="mobile-menu-toggle"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  aria-label="Toggle menu"
                >
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </button>
              </div>

            </header>

            {/* Click overlay to close dropdowns */}
            {(showUserDropdown || showMobileMenu || showBitcoinMenu || showBooksMenu) && (
              <div 
                className="overlay" 
                onClick={() => {
                  setShowUserDropdown(false);
                  setShowMobileMenu(false);
                  setShowBitcoinMenu(false);
                  setShowBooksMenu(false);
                }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: showMobileMenu ? 999 : 100,
                  background: showMobileMenu ? 'rgba(0, 0, 0, 0.8)' : 'transparent'
                }}
              />
            )}

            {/* Mobile Menu Overlay */}
            {showMobileMenu && (
              <div className="mobile-menu-overlay">
                <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="mobile-menu-header">
                    <h3>Platform Features</h3>
                    <button 
                      className="close-mobile-menu"
                      onClick={() => setShowMobileMenu(false)}
                      aria-label="Close menu"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="mobile-menu-content">
                    {isAuthenticated && (
                      <>
                        <div className="mobile-menu-section">
                          <h4>My Documents</h4>
                          <button 
                            className="mobile-menu-item"
                            onClick={() => {
                              setCurrentDocument(null);
                              setShowExchange(false);
                              setShowMobileMenu(false);
                            }}
                          >
                            📄 New Document
                          </button>
                          {/* DocumentSidebar removed for bookstore - not needed for book browsing */}
                        </div>

                        <div className="mobile-menu-section">
                          <h4>Document Actions</h4>
                          <button className="mobile-menu-item">
                            💾 Save to Blockchain
                          </button>
                          <button className="mobile-menu-item">
                            🌍 Publish Document
                          </button>
                        </div>

                        <div className="mobile-menu-section">
                          <h4>Security & Monetization</h4>
                          <button className="mobile-menu-item">
                            🔒 Encrypt Document
                          </button>
                          <button className="mobile-menu-item">
                            💰 Set Price to Unlock
                          </button>
                          <button className="mobile-menu-item">
                            🎨 Save as Bitcoin OS Asset
                          </button>
                          <button className="mobile-menu-item">
                            📈 Issue File Shares
                          </button>
                        </div>

                        <div className="mobile-menu-section">
                          <h4>Blockchain Storage</h4>
                          <button className="mobile-menu-item">
                            ⚡ OP_RETURN (Fast)
                          </button>
                          <button className="mobile-menu-item">
                            🔐 OP_PUSHDATA4 (Secure)
                          </button>
                          <button className="mobile-menu-item">
                            🧩 Multisig P2SH
                          </button>
                        </div>
                      </>
                    )}

                    <div className="mobile-menu-section">
                      <h4>Help & Info</h4>
                      <button className="mobile-menu-item">
                        ❓ How It Works
                      </button>
                      <button className="mobile-menu-item">
                        💡 Storage Options Guide
                      </button>
                      <button className="mobile-menu-item">
                        📊 Pricing Calculator
                      </button>
                    </div>

                    {!isAuthenticated && (
                      <div className="mobile-menu-section">
                        <button 
                          className="mobile-menu-login"
                          onClick={() => {
                            handcashService.login();
                            setShowMobileMenu(false);
                          }}
                        >
                          🔑 Sign in with bWalletX
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className={`app-container ${isInOS ? '' : (!isMobile && devSidebarCollapsed ? 'with-dev-sidebar-collapsed' : '')} ${isInOS ? '' : (!isMobile && !devSidebarCollapsed ? 'with-dev-sidebar' : '')}`}>
              {/* DocumentSidebar removed for bookstore - not needed for book browsing */}
              <main>
                {showFeatures ? (
                  <div className="features-view-wrapper">
                    <button 
                      className="features-close-btn"
                      onClick={() => setShowFeatures(false)}
                      title="Close Features"
                    >
                      ✕
                    </button>
                    <FeaturesPage />
                  </div>
                ) : showExchange ? (
                  <DocumentExchangeView 
                    onSelectDocument={(doc) => {
                      console.log('Selected document from exchange:', doc);
                      // Could open the document in an editor modal or side panel
                    }}
                    userDocuments={publishedDocuments} // Documents published from sidebar
                    onClose={() => setShowExchange(false)} // Return to editor view
                  />
                ) : showBitcoinApps ? (
                  <BitcoinAppsView 
                    isOpen={showBitcoinApps}
                    onClose={() => setShowBitcoinApps(false)}
                  />
                ) : activeAppOverview ? (
                  <BitcoinAppOverviews
                    activeApp={activeAppOverview}
                    onClose={() => setActiveAppOverview(null)}
                  />
                ) : (
                  <BookStore 
                    isAuthenticated={isAuthenticated}
                    onCartUpdate={(itemCount) => {
                      // Update cart count in taskbar
                      console.log('Cart updated:', itemCount);
                    }}
                  />
                )}
              </main>
            </div>
            <Footer />
          </div>
          </>
        )}
      />
      </Routes>
    </GoogleAuthProvider>
    </>
  );
}

export default App;